import {Card} from 'platform/components';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCheckoutBillingInformationQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RequiredTestIdProps} from 'shared/web';

import {CheckoutCardFrame} from './CheckoutCardFrame';

interface AftersalesCheckoutBillingCardProps extends RequiredTestIdProps {
  checkoutId: string;
  isSideBySide: boolean;
  children: ReactNode;
}

export function AftersalesCheckoutBillingCard(props: AftersalesCheckoutBillingCardProps) {
  const {data: billingInformation, isError} = useGetCheckoutBillingInformationQuery({
    checkoutId: props.checkoutId,
  });
  const contractId = billingInformation?.billingInformation.contractInformation?.id;

  const isLoaded = isNotNil(billingInformation) || isError;
  // Issue payment needs a contract, so the collapsed card opens by itself when there is none, or
  // when the billing information failed to load and its error would stay hidden.
  const isOpenByDefault = isLoaded && (isError || isNilOrEmpty(contractId));

  const title = i18n.t('entity.checkout.labels.billingInformation');

  // The two layouts render different cards, so crossing the layout width remounts the billing
  // information. Its only unsaved state is a half-filled new contract form, and the collapsed
  // stacked card unmounts that anyway.
  if (props.isSideBySide) {
    return (
      <CheckoutCardFrame
        variant="inlineWhite"
        title={title}
        isFullHeight
        data-testid={props['data-testid']}
      >
        {props.children}
      </CheckoutCardFrame>
    );
  }

  return (
    <Card
      // The card opens by default only once the billing information has loaded. After that the
      // user's toggle wins, so picking a contract does not close the card under the user's hands.
      key={isLoaded ? 'loaded' : 'loading'}
      variant="inlineWhite"
      title={title}
      isExpandable
      isClosedByDefault={!isOpenByDefault}
      hasSeparator
      data-testid={props['data-testid']}
    >
      {props.children}
    </Card>
  );
}
